import React, { useRef, useState } from "react";
import { Card, Flex, Typography, Tag, theme, Dropdown, Button, Divider } from "antd";
import { useSwipeable } from "react-swipeable";
import {
  TagOutlined,
  ToolOutlined,
  EyeOutlined,
  EditOutlined,
  MoreOutlined,
  BarcodeOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import { useHybridResponsive } from "../../shared/hooks";
import type { ProductData } from "../types";

const { Text } = Typography;

// ── Constants ─────────────────────────────────────────────────────────────────

const SNAP_THRESHOLD = 40;
const ACTION_WIDTH = 68;

// ── Product type config ───────────────────────────────────────────────────────

const TYPE_CONFIG = {
  Producto: {
    tagColor: "blue",
    icon: <TagOutlined />,
    accentColor: (t: any) => t.colorPrimary,
    bgColor: (t: any) => t.colorPrimaryBg,
    iconColor: (t: any) => t.colorPrimary,
  },
  Servicio: {
    tagColor: "purple",
    icon: <ToolOutlined />,
    accentColor: (_t: any) => "#722ed1",
    bgColor: (_t: any) => "#f9f0ff",
    iconColor: (_t: any) => "#722ed1",
  },
} as const;

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * La tarjeta también se usa para las líneas de una cotización, que solo traen
 * parte de los datos del catálogo: por eso el producto se acepta parcial.
 */
export type ProductLike = Partial<Omit<ProductData, "product_iva" | "product_iva_percentage">> & {
  product_name: string;
  product_iva?: string | number | null;
  product_iva_percentage?: string | number | null;
};

interface ItemProductProps {
  product: ProductLike;
  onView?: () => void;
  onEdit?: () => void;
  onClick?: () => void;
  footer?: React.ReactNode;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatPrice = (price: number) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(price);

/** Un producto lleva IVA solo si tiene un porcentaje configurado mayor a cero. */
const resolveIva = (percentageIva: string | number | null | undefined) =>
  percentageIva !== null && percentageIva !== undefined && percentageIva !== "" && Number(percentageIva) > 0;

// ── Swipe action button ───────────────────────────────────────────────────────

const SwipeBtn: React.FC<{
  icon: React.ReactNode;
  label: string;
  bg: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ icon, label, bg, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", gap: 4, width: ACTION_WIDTH, height: "99%",
      border: "none", background: bg, color: "#fff", fontSize: 11,
      fontWeight: 700, cursor: "pointer", padding: 0, flexShrink: 0,
    }}
  >
    <span style={{ fontSize: 18 }}>{icon}</span>
    {label}
  </button>
);

// ── Card inner content ────────────────────────────────────────────────────────

const CardContent: React.FC<{
  product: ProductLike;
  token: any;
  dropdownMenu?: React.ReactNode;
}> = ({ product, token, dropdownMenu }) => {
  const typeCfg = TYPE_CONFIG[product.product_type ?? "Producto"] ?? TYPE_CONFIG.Producto;
  const hasIva = resolveIva(product.product_iva_percentage);
  const ivaLabel = hasIva
    ? `IVA ${product.product_iva_percentage}%`
    : "Sin IVA";

  return (
    <Flex align="flex-start" justify="space-between" gap={12}>
      {/* Left: icon + details */}
      <Flex align="flex-start" gap={12} style={{ flex: 1, minWidth: 0 }}>
        {/* Type icon */}
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: token.borderRadiusLG,
            backgroundColor: typeCfg.bgColor(token),
            color: typeCfg.iconColor(token),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            flexShrink: 0,
          }}
        >
          {typeCfg.icon}
        </div>

        {/* Text stack */}
        <Flex vertical gap={5} style={{ minWidth: 0, flex: 1 }}>
          {/* Badges row */}
          <Flex align="center" gap={6} wrap>
            <Tag
              color={typeCfg.tagColor}
              style={{ margin: 0, lineHeight: "18px", fontWeight: 600 }}
            >
              {product.product_type}
            </Tag>
            <Tag
              style={{
                margin: 0,
                fontSize: 10,
                lineHeight: "18px",
                backgroundColor: hasIva ? token.colorSuccessBg : token.colorWarningBg,
                color: hasIva ? token.colorSuccess : token.colorWarning,
                borderColor: hasIva ? token.colorSuccessBorder : token.colorWarningBorder,
              }}
            >
              <PercentageOutlined style={{ marginRight: 2, fontSize: 9 }} />
              {ivaLabel}
            </Tag>
          </Flex>

          {/* Product name */}
          <Text
            strong
            style={{
              color: token.colorText,
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {product.product_name}
          </Text>

          {/* SAT key */}
          {product.product_key_sat && <Flex align="center" gap={5}>
            <BarcodeOutlined style={{ fontSize: 11, color: token.colorTextTertiary }} />
            <Text
              style={{
                fontSize: 11,
                color: token.colorTextSecondary,
                fontFamily: "monospace",
                letterSpacing: "0.04em",
              }}
            >
              {product.product_key_sat}
            </Text>
          </Flex>}
        </Flex>
      </Flex>

      {/* Right: price + actions */}
      <Flex vertical align="flex-end" justify="space-between" gap={6} style={{ flexShrink: 0 }}>
        <Text
          strong
          style={{
            fontSize: 16,
            color: token.colorText,
            letterSpacing: "-0.02em",
            whiteSpace: "nowrap",
          }}
        >
          {formatPrice(Number(product.product_price))}
        </Text>
        {dropdownMenu}
      </Flex>
    </Flex>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const ItemProduct: React.FC<ItemProductProps> = ({ product, onView, onEdit, onClick, footer = null }) => {
  console.log("producttt", product);

  const { token } = theme.useToken();
  const { isMobile } = useHybridResponsive();

  const [offsetX, setOffsetX] = useState(0);
  const startX = useRef(0);
  const isDragging = useRef(false);

  const typeCfg = TYPE_CONFIG[product.product_type ?? "Producto"] ?? TYPE_CONFIG.Producto;
  const accentColor = typeCfg.accentColor(token);

  const rightActions = [
    onEdit && { icon: <EditOutlined />, label: "Editar", bg: token.colorWarning, handler: onEdit },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; bg: string; handler: () => void }[];

  const leftActions = [
    onView && { icon: <EyeOutlined />, label: "Ver", bg: token.colorPrimary, handler: onView },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; bg: string; handler: () => void }[];

  const maxRight = rightActions.length * ACTION_WIDTH;
  const maxLeft = leftActions.length * ACTION_WIDTH;

  const snapTo = (target: number) => { setOffsetX(target); isDragging.current = false; };
  const close = () => snapTo(0);

  const swipeHandlers = useSwipeable({
    onSwipeStart: () => { startX.current = offsetX; isDragging.current = true; },
    onSwiping: ({ deltaX }) => {
      if (!isDragging.current) return;
      const clamped = Math.max(-maxRight, Math.min(maxLeft, startX.current + deltaX));
      setOffsetX(clamped);
    },
    onSwiped: ({ deltaX }) => {
      const total = startX.current + deltaX;
      if (total < -SNAP_THRESHOLD && maxRight > 0) snapTo(-maxRight);
      else if (total > SNAP_THRESHOLD && maxLeft > 0) snapTo(maxLeft);
      else snapTo(0);
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: true,
  });

  const sharedCardStyle = {
    borderRadius: token.borderRadiusLG,
    borderLeft: `3px solid ${accentColor}`,
    transition: "all 0.2s ease",
  };

  const bodyStyle = { padding: "14px 18px" };

  // ── Desktop ───────────────────────────────────────────────────────────────

  if (!isMobile) {
    const dropdownItems: any[] = [];
    if (onView)
      dropdownItems.push({
        key: "view",
        icon: <EyeOutlined style={{ color: token.colorPrimary }} />,
        label: "Ver detalle",
        onClick: (info: any) => { info.domEvent?.stopPropagation(); onView(); },
      });
    if (onEdit)
      dropdownItems.push({
        key: "edit",
        icon: <EditOutlined style={{ color: token.colorWarning }} />,
        label: "Editar",
        onClick: (info: any) => { info.domEvent?.stopPropagation(); onEdit(); },
      });

    const dropdownMenu =
      dropdownItems.length > 0 ? (
        <Dropdown menu={{ items: dropdownItems }} trigger={["click"]} placement="bottomRight">
          <Button
            type="text"
            shape="circle"
            icon={<MoreOutlined style={{ fontSize: 16 }} />}
            onClick={(e) => e.stopPropagation()}
            style={{ color: token.colorTextSecondary }}
          />
        </Dropdown>
      ) : null;

    return (
      <Card
        hoverable
        onClick={onClick}
        style={{ ...sharedCardStyle, marginBottom: token.marginSM }}
        styles={{ body: bodyStyle }}
      >
        <CardContent product={product} token={token} dropdownMenu={dropdownMenu} />
        {footer && <Divider />}
        {footer}
      </Card>
    );
  }

  // ── Mobile: no swipe actions → plain card ────────────────────────────────

  if (!onView && !onEdit) {
    return (
      <Card
        hoverable
        onClick={onClick}
        style={{ ...sharedCardStyle, marginBottom: token.marginSM }}
        styles={{ body: bodyStyle }}
      >
        <CardContent product={product} token={token} />
        {footer && <Divider />}
        {footer}
      </Card>
    );
  }

  // ── Mobile: swipeable card ────────────────────────────────────────────────

  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: token.borderRadiusLG,
        marginBottom: token.marginSM,
      }}
    >
      {/* Left reveal (View) */}
      {leftActions.length > 0 && (
        <div
          style={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            display: "flex", width: maxLeft,
          }}
        >
          {leftActions.map((a) => (
            <SwipeBtn
              key={a.label}
              icon={a.icon}
              label={a.label}
              bg={a.bg}
              onClick={(e) => { e.stopPropagation(); a.handler(); close(); }}
            />
          ))}
        </div>
      )}

      {/* Right reveal (Edit) */}
      {rightActions.length > 0 && (
        <div
          style={{
            position: "absolute", right: 0, top: 0, bottom: 0,
            display: "flex", width: maxRight,
          }}
        >
          {rightActions.map((a) => (
            <SwipeBtn
              key={a.label}
              icon={a.icon}
              label={a.label}
              bg={a.bg}
              onClick={(e) => { e.stopPropagation(); a.handler(); close(); }}
            />
          ))}
        </div>
      )}

      {/* Sliding card */}
      <div
        {...swipeHandlers}
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: isDragging.current ? "none" : "transform 0.25s ease",
          willChange: "transform",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Card
          hoverable
          onClick={offsetX === 0 ? onClick : close}
          style={sharedCardStyle}
          styles={{ body: bodyStyle }}
        >
          <CardContent product={product} token={token} />
        </Card>
      </div>
    </div>
  );
};

export default ItemProduct;
export type { ItemProductProps };
